import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, pin) => {
    try {
      const body = pin ? { pin } : { email, password };
      const response = await axios.post(`${API_URL}/api/auth/login`, body, { withCredentials: true });
      setUser(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiError(error.response?.data?.detail) 
      };
    }
  };

  const clientLogin = async (phone, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/client-login`, {
        phone,
        password
      }, { withCredentials: true });
      setUser(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiError(error.response?.data?.detail) 
      };
    }
  };

  const clientRegister = async (name, phone, email, password, referralCode) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/client-register`, {
        name,
        phone,
        email: email || null,
        password,
        referral_code: referralCode || null
      }, { withCredentials: true });
      setUser(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiError(error.response?.data?.detail) 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {

    }
    setUser(false);
  };

  const value = {
    user,
    loading,
    login,
    clientLogin,
    clientRegister,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isCajero: user?.role === 'cajero',
    isOperador: user?.role === 'operador',
    isGerente: user?.role === 'gerente',
    isCliente: user?.role === 'cliente',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

function formatApiError(detail) {
  if (detail == null) return 'Algo salió mal. Por favor intenta de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default AuthContext;
